import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchProductsFromSheet } from "../../utils/sheetProducts";
import { ProductImageWithFallbacks } from "../ProductImageWithFallbacks";
import { formatInrAmount } from "../../utils/formatInrAmount";
import { BtnContent } from "../BtnIcon";
import { IoCartOutline } from "react-icons/io5";

export default function FeaturedProductsHome({ addToCart }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProductsFromSheet();
        if (cancelled) return;
        const featured = list
          .filter((p) => p.name && Number(p.price) > 0)
          .sort((a, b) => Number(b.stock || 0) - Number(a.stock || 0))
          .slice(0, 6);
        setProducts(featured);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Could not load products.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="home-featured" aria-labelledby="home-featured-heading">
      <div className="lux-section-head">
        <p className="lux-eyebrow">Signature collection</p>
        <h2 id="home-featured-heading" className="lux-h2">
          Curated boxes, ready to gift
        </h2>
        <p className="lux-lead">Handpicked from our catalogue — tap to view or add to cart.</p>
      </div>

      {loading ? (
        <div className="home-featured__grid" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="home-featured__skeleton">
              <div className="home-featured__skeleton-media" />
              <div className="home-featured__skeleton-line" />
              <div className="home-featured__skeleton-line home-featured__skeleton-line--short" />
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="home-featured__error">{error}</p>
      ) : (
        <div className="home-featured__grid">
          {products.map((p, index) => (
            <motion.article
              key={p.id}
              className="home-featured__card"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-5%" }}
              transition={{ duration: 0.35, delay: index * 0.04 }}
            >
              <div className="home-featured__media-wrap">
                <Link to={`/product/${p.id}`} className="home-featured__media">
                  <ProductImageWithFallbacks
                    urls={p.images}
                    alt={`${p.name} curated gift box — Rencin Gifting`}
                    className="home-featured__image"
                  />
                  {p.hasDiscount && p.discountPercent > 0 ? (
                    <span className="home-featured__badge">{Math.round(p.discountPercent)}% off</span>
                  ) : null}
                </Link>
                {addToCart && Number(p.stock) > 0 ? (
                  <button
                    type="button"
                    className="home-featured__quick-add"
                    onClick={() =>
                      addToCart({
                        id: p.id,
                        name: p.name,
                        price: p.price,
                        image: p.images?.[0] || "",
                        description: p.description,
                      })
                    }
                  >
                    <BtnContent icon={IoCartOutline} iconSize={14}>
                      Add to cart
                    </BtnContent>
                  </button>
                ) : null}
              </div>
              <div className="home-featured__body">
                {p.color ? <p className="home-featured__meta">{p.color}</p> : null}
                <Link to={`/product/${p.id}`} className="home-featured__title">
                  <h3>{p.name}</h3>
                </Link>
                <div className="home-featured__footer">
                  <p className="home-featured__price">
                    {p.hasDiscount && p.listPrice > p.price ? (
                      <span className="home-featured__compare">₹{formatInrAmount(p.listPrice)}</span>
                    ) : null}
                    <span className="home-featured__amount">₹{formatInrAmount(p.price)}</span>
                  </p>
                  <Link to={`/product/${p.id}`} className="home-featured__view">
                    View
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      )}

      <div className="home-featured__more">
        <Link to="/shop" className="lux-btn lux-btn--primary">
          View all gift boxes
        </Link>
      </div>
    </section>
  );
}
